'use strict';

/**
 * The helpers exported in this file are used to calculate
 * the material details that will appear on the pdp.
 * Copy for the details titles is not determined in these helpers.
 * These methods return only the information that will
 * appear under a detail's title.
 */

const findNameSpace = require('find-namespace-value');
const helpers = require('../helpers/detailsSharedLogic');

/**
 * Checks if an object, or nested objects under the key 'parent', have
 * a name value that matches the one passed as a param
 *
 * @param {object} parent
 * @param {string} parent.name
 * @param {object} parent.parent
 * @param {string} name           - string to compare parent.name against
 * @returns {boolean}             - true if parent.name = name, false otherwise
 */
function hasAncestor(parent, name) {
    const _name = findNameSpace('name', parent);
    const _parent = findNameSpace('parent', parent);

    if (_name && _name.toLowerCase() === name) {
        return true;
    } else if (_parent) {
        return hasAncestor(_parent, name);
    }

    return false;
}

/**
 * Get the item's material attributes that have a metal ancestor
 *
 * @param item       - Bunsen Item model
 * @returns {Array}  - item's material objects that are metals
 */
function getMetalModels(item) {
    const materials = item.get('attributes.material');
    const metals = [];

    if (!materials) {
        return metals;
    }

    materials.each(material => {
        const parent = material.get('option.parent');

        if (hasAncestor(parent, 'metal')) {
            metals.push(material);
        }
    });

    return metals;
}

/**
 * Get the item's material attributes that have a stone ancestor
 *
 * @param item       - Bunsen Item model
 * @returns {Array}  - item's material objects that are stones
 */
function getStoneModels(item) {
    const materials = item.get('attributes.material');
    const stones = [];

    if (!materials) {
        return stones;
    }

    materials.each(material => {
        const parent = material.get('option.parent');

        if (hasAncestor(parent, 'stone')) {
            stones.push(material);
        }
    });

    return stones;
}
/**
 *
 * Get an array of objects that DetailsItem.jsx will use to render a detail. Each object will have a key
 * 'value', which holds the display name of a material.
 *
 * @param {object} config
 * @param {object} config.item      - Bunsen Item Model
 * @param {string} config.linksKey  - the key under which links for this detail can be found in config.links
 * @param {object} config.links     - see this.detailLinks in ItemStore for definition {@link newPdp/ItemStore}
 * @param {Function} config.getter  - a function for grabbing materials from the item
 *
 * @returns {{
 *               value: string,
 *               isLink: boolean,
 *               link: string
 *          }[]}
 *
 * ie: [{
 *          value: '18k Gold',
 *          isLink: true,
 *          link: '/jewelry/rings/cocktail-rings/metal/18k-gold/'
 *     }]
 */
function getMaterialByMethod(config) {
    const contents = [];
    const { item, links, linkKey, getter } = config;
    // Grab the materials you want to return info for
    // in this method
    const materials = getter(item);
    let material;

    for (material of materials) {
        const content = { isLink: false };
        let link;

        // Display name for material
        content.value = material.get('option.name');

        // Check if there is a link for the type of detail
        // ie links && links['metal']
        if (links && links[linkKey]) {
            const value = content.value;
            // Grab the link for that type of detail and the material's display name
            // ie links['metal'] = {'18k Gold': '/jewelry/rings/cocktail-rings/metal/18k-gold/'} so
            // link = links['metal']['18k Gold']
            link = links[linkKey][value];
        }

        if (link) {
            content.isLink = true;
            content.link = link;
        }

        if (content.value) {
            contents.push(content);
        }
    }

    return contents;
}

/**
 * Get an attribute's group key. If the groupKey is
 * undefined, a default value 'noGroupKey' will be returned.
 *
 * @param {object} model - Bunsen ItemAttribute model
 * @returns {string}
 */
function materialsAndTechniquesGroup(model) {
    const groupKey = model.get('groupKey');
    if (groupKey) {
        return groupKey;
    }

    return 'noGroupKey';
}

/**
 * Get a callback to be used on an ItemAttributes collection's each method for the purpose
 * of creating an array of arrays containing objects with info that should appear for a detail
 *
 * @param {object} config
 * @param {object} config.contents                - array containing arrays of objects with info that
 *                                                  should appear for a detail
 * @param {object} config.hash                    - hash used to check if a material has already been added
 *                                                  to config.contents
 * @param {boolean} config.hash.[attribute name]  - true if attribute is already in config.contents, false otherwise
 * @param {object} config.links                   - see this.detailLinks in ItemStore for definition {@link newPdp/ItemStore}
 * @returns {Function}
 */
function getMaterialsAndTechniquesContentCallback(config) {
    const { contents, hash, links } = config;

    return attribute => {
        const content = {
            isLink: false,
            value: attribute.get('option.name'),
        };
        let link;

        if (hash[content.value]) {
            return;
        }

        if (links && links.materialsAndTechniques) {
            const value = content.value;
            link = links.materialsAndTechniques[value];
        }

        if (link) {
            content.isLink = true;
            content.link = link;
        }

        if (content.value) {
            contents.push([content]);
        }
    };
}

/**
 * Get an array containing one object with display info for a technique and
 * another object with display info for a material in that order. Returns an
 * empty array if the material or technique's display name is missing.
 *
 * @param config
 * @param {object} config.material   - Bunsen ItemAttributes model
 * @param {object} config.technique  - Bunsen ItemAttributes model
 * @param {object} config.links      - see this.detailLinks in ItemStore for definition {@link newPdp/ItemStore}
 * @returns {Array}
 */
function formatMaterialAndTechnique(config) {
    const { material, technique, links } = config;

    const content = [];
    const techniqueContent = {
        isLink: false,
        value: technique.get('option.name'),
    };
    const materialContent = {
        isLink: false,
        value: material.get('option.name'),
    };

    let techniqueLink;
    let materialLink;

    if (links && links.materialsAndTechniques) {
        const techniqueValue = techniqueContent.value;
        const materialValue = materialContent.value;
        techniqueLink = links.materialsAndTechniques[techniqueValue];
        materialLink = links.materialsAndTechniques[materialValue];
    }

    if (techniqueLink) {
        techniqueContent.isLink = true;
        techniqueContent.link = techniqueLink;
    }

    if (materialLink) {
        materialContent.isLink = true;
        materialContent.link = materialLink;
    }

    // Technique should be pushed first
    if (techniqueContent.value && materialContent.value) {
        content.push(techniqueContent);
        content.push(materialContent);
    }

    return content;
}

module.exports = {
    /**
     *
     * Get an array of arrays containing objects that DetailsMaterialsAndTechniques.jsx
     * will use to render this detail. Each object will have a key 'value', which holds the
     * display name of a material or technique.
     *
     * @param {object} config
     * @param {object} config.item   - Bunsen Item Model
     * @param {object} config.links  - see this.detailLinks in ItemStore for definition {@link newPdp/ItemStore}
     *
     * @returns {{
     *               value: string,
     *               isLink: boolean,
     *               link: string,
     *               extraCopy: string
     *          }[][]}
     *
     * ie: [
     *         [
     *             {
     *                 isLink: true,
     *                 value: 'Polished',
     *                 link: '/furniture/storage-case-pieces/credenzas/technique/polished/'
     *             },
     *             {
     *                 isLink: true,
     *                 value: 'Brass',
     *                 link: '/furniture/storage-case-pieces/credenzas/material/brass/'
     *             }
     *         ],
     *         [
     *             {
     *                 isLink: true,
     *                 value: 'Walnut',
     *                 link: '/furniture/storage-case-pieces/credenzas/material/walnut/'
     *             }
     *         ]
     *     ]
     */
    getMaterialsAndTechniques: function(config) {
        const { item, links } = config;
        const contents = [];

        const techniques = item.get('attributes.technique');
        const materials = item.get('attributes.material');

        // Hashes track what materials and techniques have been paired
        const techniquesHash = {};
        const materialsHash = {};

        if (materials && techniques) {
            // Get an object in which the keys are group keys and the values are arrays
            // containing all models with that group key.
            const groupedMaterials = materials.groupBy(materialsAndTechniquesGroup);
            const groupedTechniques = techniques.groupBy(materialsAndTechniquesGroup);
            const keys = Object.keys(groupedMaterials);
            // Get the keys for which there are a material and a technique with the same group key
            const filteredKeys = keys.filter(key => {
                return (
                    key !== 'noGroupKey' &&
                    groupedMaterials[key][0] &&
                    groupedTechniques[key] &&
                    groupedTechniques[key][0]
                );
            });

            // Material and techniques are supposed to appear on the pdp like so:
            // Technique + Material if the two have the same group key.
            // ie: 'Carved Giltwood '
            filteredKeys.forEach(key => {
                const material = groupedMaterials[key][0];
                const technique = groupedTechniques[key][0];

                materialsHash[material.get('option.name')] = true;
                techniquesHash[technique.get('option.name')] = true;
                // Get a subarray with the material and technique
                // ie [{value: 'material'}, {value: 'technique'}]
                const formattedInfo = formatMaterialAndTechnique({ material, technique, links });

                if (formattedInfo) {
                    contents.push(formattedInfo);
                }
            });
        }

        // Get a function that will return a subarray for a technique that isn't paired
        // ie: [{value: 'technique'}]
        const techniquesCallback = getMaterialsAndTechniquesContentCallback({
            contents,
            hash: techniquesHash,
            links,
        });

        // Material and techniques are supposed to appear on the pdp like so:
        // Technique if the technique hasn't already been paired with a material
        // ie: 'Polished'
        if (techniques) {
            techniques.each(techniquesCallback);
        }

        // Get a function that will return a subarray for a material that isn't paired
        // ie: [{value: 'material'}]
        const materialsCallback = getMaterialsAndTechniquesContentCallback({
            contents,
            hash: materialsHash,
            links,
        });

        // Material and techniques are supposed to appear on the pdp like so:
        // Material if the material hasn't already been paired with a technique
        // ie: 'Marble'
        if (materials) {
            materials.each(materialsCallback);
        }

        return contents;
    },

    /**
     * See definition for private method getCreatorsByAttribution above
     */
    getMetals: function(config) {
        const inflatedConfig = Object.assign(
            {
                linkKey: 'metal',
                getter: getMetalModels,
            },
            config
        );
        return getMaterialByMethod(inflatedConfig);
    },

    /**
     * See definition for private method getCreatorsByAttribution above
     */
    getStones: function(config) {
        const inflatedConfig = Object.assign(
            {
                linkKey: 'stone',
                getter: getStoneModels,
            },
            config
        );
        return getMaterialByMethod(inflatedConfig);
    },

    getStoneCut: function(config) {
        const { item, links } = config;

        if (links && typeof links.stoneCut === 'object') {
            const name = Object.keys(links.stoneCut)[0];
            return [
                {
                    isLink: true,
                    value: name,
                    link: links.stoneCut[name],
                },
            ];
        } else {
            const stoneCuts  = item.get('attributes.stone-cut');
            if (stoneCuts && !!stoneCuts.length) {
                return [
                    {
                        isLink: false,
                        value: stoneCuts[0].value,
                    },
                ];
            }
        }
        return null;
    },

    /**
     * If carat weight exists in "links" object (falls into predefined carat buckets),
     * it's then being taken from there.
     * Otherwise it's shown as simple text value, taken from carat weight attribute.
     */
    getCaratWeight: function(config) {
        const { item, links } = config;
        if (links && typeof links.caratWeight === 'object') {
            const name = Object.keys(links.caratWeight)[0];
            return [
                {
                    isLink: true,
                    value: name,
                    link: links.caratWeight[name],
                },
            ];
        } else {
            const caratWeights = item.get('attributes.carat-weight');
            if (caratWeights) {
                return [
                    {
                        isLink: false,
                        value: `${caratWeights[0].text}ct`,
                    },
                ];
            }
        }
        return null;
    },

    /**
     * Get an array of objects that DetailsItem.jsx will use to render a detail. Each object will have a key
     * 'value', which holds the display data for a medium.
     *
     * @param {object} config
     * @param {object} config.item      - Bunsen Item Model
     * @param {string} config.linksKey  - the key under which links for this detail can be found in config.links
     * @param {object} config.links     - see this.detailLinks in ItemStore for definition {@link newPdp/ItemStore}
     *
     * @returns {{
     *               value: string,
     *               isLink: boolean,
     *               link: string
     *          }[]}
     *
     * ie: [{
     *          value: 'Archival Pigment Print',
     *          isLink: true,
     *          link: '/art/photography/material/archival-pigment-print/'
     *     }]
     */
    getMediums: function(config) {
        const contents = [];
        const item = config.item;
        const links = config.links;
        const materials = item.get('attributes.material');

        if (!materials) {
            return contents;
        }

        materials.each(material => {
            const content = { isLink: false };
            let link;

            content.value = material.get('option.name');

            if (links && links.medium) {
                const value = content.value;
                link = links.medium[value];
            }

            if (link) {
                content.isLink = true;
                content.link = link;
            }

            if (content.value) {
                contents.push(content);
            }
        });

        return contents;
    },

    /**
     * See definition for addToContents in detailsSharedLogic {@link newPdp/details/helpers/detailsSharedLogic}
     */
    getMaterialsNotes: function(config) {
        // The service response for a fashion item on a pdp returns only the ids for
        // techniques. We then make a queryservice call to find them. Since there is no proper
        // way to add that queryservice response to an instance of Bunsen/Models/Inventory/Item.js,
        // the display value for the material notes detail was just set as a server var (detailTechniques).
        // Rather than getting that info from an instance of Item.js, like for other details.
        const techniques = config.detailTechniques;
        const inflatedConfig = Object.assign(
            {
                passedValue: techniques,
            },
            config
        );

        return helpers.addToContents(inflatedConfig);
    },
};



// WEBPACK FOOTER //
// ./assets/js/newPdp/details/helpers/detailsMaterials.es.js